import api from './api';
import {
  ApiResponse,
  Flag,
  Dataset,
  Plate,
  PlateRegion,
} from '../types';

// Search payload type
export interface PlateSearchPayload {
  dataset?: string;
  region?: string;
  tag?: string;
  flags?: string[];
  sorting?: 'normal' | 'reverse' | 'random';
  pageSize?: number;
  page?: number;
}

// Search response type
export interface PlateSearchResponse {
  plates: Plate[];
  items?: Plate[];
  totalResults: number;
  page: number;
  pageSize: number;
}

// Patch payload type (uses {id} wrapper for reference fields)
export interface PlatePatchPayload {
  tag?: string;
  region?: { id: string };
  dataset?: { id: string };
  flags?: { id: string }[];
  corners?: number[][];
}

// Dropdown data response type
export interface PlateDropdownData {
  regions: PlateRegion[];
  flags: Flag[];
  datasets: Dataset[];
}

/**
 * Fetches all dropdown data for plate search/edit forms.
 */
export async function fetchPlateDropdownData(signal?: AbortSignal): Promise<PlateDropdownData> {
  const config = signal ? { signal } : {};

  const [regionsRes, flagsRes, datasetsRes] = await Promise.all([
    api.get<ApiResponse<PlateRegion[]>>('plate/list/regions', config),
    api.get<ApiResponse<Flag[]>>('plate/list/flags', config),
    api.get<ApiResponse<Dataset[]>>('datasets/list/plate', config),
  ]);

  return {
    regions: regionsRes.data.results || [],
    flags: flagsRes.data.results || [],
    datasets: datasetsRes.data.results || [],
  };
}

/**
 * Searches for plates using the given payload.
 */
export async function searchPlates(payload: PlateSearchPayload, signal?: AbortSignal): Promise<PlateSearchResponse> {
  const config = signal ? { signal } : {};
  const response = await api.post<ApiResponse<PlateSearchResponse>>('plate/search', payload, config);
  const data = response.data.results;

  return {
    plates: data.items || [],
    totalResults: data.totalResults || 0,
    page: data.page || 1,
    pageSize: data.pageSize || 20,
  };
}

/**
 * Updates a plate with the given patch data.
 */
export async function patchPlate(plateId: string, patch: PlatePatchPayload): Promise<void> {
  await api.patch(`plate/patch/${plateId}`, patch);
}

/**
 * Updates multiple plates with the same patch data.
 * Uses Promise.allSettled for parallel requests.
 */
export async function patchPlates(
  plateIds: string[],
  patch: PlatePatchPayload
): Promise<{ succeeded: string[]; failed: string[] }> {
  const results = await Promise.allSettled(
    plateIds.map((id) => api.patch(`plate/patch/${id}`, patch))
  );

  const succeeded: string[] = [];
  const failed: string[] = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeeded.push(plateIds[index]);
    } else {
      failed.push(plateIds[index]);
    }
  });

  return { succeeded, failed };
}

/**
 * Deletes a single plate.
 * Admin only.
 */
export async function deletePlate(plateId: string): Promise<void> {
  await api.delete(`plate/delete/${plateId}`);
}

/**
 * Deletes multiple plates.
 * Uses Promise.allSettled for parallel requests.
 * Admin only.
 */
export async function deletePlates(
  plateIds: string[]
): Promise<{ succeeded: string[]; failed: string[] }> {
  const results = await Promise.allSettled(
    plateIds.map((id) => api.delete(`plate/delete/${id}`))
  );

  const succeeded: string[] = [];
  const failed: string[] = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeeded.push(plateIds[index]);
    } else {
      failed.push(plateIds[index]);
    }
  });

  return { succeeded, failed };
}
